/**
 * The `season` slot's body as a pure function of props: the current
 * season line, the gate block, and the primary action. No fetching, no
 * mutation, no URL access; `PoolSeasonCardContainer` owns all of that.
 */
import { Button, InlineBanner, Skeleton } from "@sportsmet/ui";
import type { JSX } from "react";

import {
  PAST_SEASONS_ROW_LABEL,
  pastSeasonsCountLabel,
} from "./pastSeasonsModel";
import {
  GATE_PROVIDER_BODY,
  GATE_PROVIDER_HEADLINE,
  GATE_UNSETTLED_ACTION_LABEL,
  GATE_UNSETTLED_HEADLINE,
  gateUnsettledBody,
  SEASON_CARD_START_LABEL,
  type typeSeasonGate,
} from "./seasonCardModel";

export type typePoolSeasonCardProps = {
  isLoading: boolean;
  currentSeasonLabel: string;
  gate: typeSeasonGate;
  onStart: () => void;
  onGoToContest: (contestId: string) => void;
  pastSeasonCount?: number | undefined;
  onOpenPastSeasons?: (() => void) | undefined;
};

export const PoolSeasonCard = ({
  isLoading,
  currentSeasonLabel,
  gate,
  onStart,
  onGoToContest,
  pastSeasonCount = 0,
  onOpenPastSeasons,
}: typePoolSeasonCardProps): JSX.Element => {
  if (isLoading) {
    return (
      <div data-testid="pool-season-loading">
        <Skeleton shape="card" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p data-testid="pool-season-current">{currentSeasonLabel}</p>
      {pastSeasonCount > 0 && onOpenPastSeasons !== undefined ? (
        <Button
          variant="secondary"
          fullWidth
          className="justify-between"
          data-testid="pool-season-past-seasons"
          onClick={onOpenPastSeasons}
        >
          <span>{PAST_SEASONS_ROW_LABEL}</span>
          <span
            className="text-caption text-ink-secondary"
            data-testid="pool-season-past-seasons-count"
          >
            {pastSeasonsCountLabel(pastSeasonCount)}
          </span>
        </Button>
      ) : null}
      {gate.kind === "providerNotReady" ? (
        <div data-testid="pool-season-gate-provider">
          <p className="text-h3 text-ink">{GATE_PROVIDER_HEADLINE}</p>
          <InlineBanner variant="warning" message={GATE_PROVIDER_BODY} />
        </div>
      ) : null}
      {gate.kind === "unsettledContest" ? (
        <div data-testid="pool-season-gate-unsettled">
          <p className="text-h3 text-ink">{GATE_UNSETTLED_HEADLINE}</p>
          <InlineBanner
            variant="warning"
            message={gateUnsettledBody({ contestName: gate.contestName })}
          />
          <Button
            variant="primary"
            data-testid="pool-season-go-to-contest"
            onClick={() => {
              onGoToContest(gate.contestId);
            }}
          >
            {GATE_UNSETTLED_ACTION_LABEL}
          </Button>
        </div>
      ) : null}
      {gate.kind === "ready" ? (
        <Button
          variant="primary"
          data-testid="pool-season-start"
          onClick={onStart}
        >
          {SEASON_CARD_START_LABEL}
        </Button>
      ) : null}
    </div>
  );
};
